import styled from 'styled-components';
import { Alert, Typography } from 'antd';
import { PageTableWrapperStyled, PageHeaderStyled } from 'styles/common.styles';
import { DETAIL_HEADER_BG } from './EmployeeSkillsBridgerV2.constants';

// ─── Employee Page Header (3-column grid, specific to this page) ──────────────

export const EmployeePageHeaderStyled = styled(PageHeaderStyled)`
  display: grid;
  grid-template-columns: 3fr minmax(100px, 1fr) 2fr;
  align-items: center;
  column-gap: 80px;
  row-gap: 16px;
  > div { flex: unset; }
  > div:first-child { flex: unset; }
  > div:last-child { flex: unset; }
`;

// ─── Employee Table Wrapper ───────────────────────────────────────────────────

export const EmployeeTableWrapperStyled = styled(PageTableWrapperStyled)`
  min-height: 0;
  overflow-y: auto;
`;

// ─── Content Grid (skills table + job description side panel) ───────────────

export const ContentGridStyled = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: ${(p) => (p.$showJd ? "1fr 0.7fr" : "1fr")};
  grid-template-rows: minmax(0, 1fr);
  overflow: hidden;
`;

// Passed to SbCard as `style` / `styles` props (antd's own customization API —
// see DETAIL_MODAL_HEADER_STYLE below for the same pattern on Modal) so the
// panel fills its grid cell and only the body scrolls, without reaching into
// antd's internal `.ant-card-*` classes.

export const JD_PANEL_CARD_STYLE = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 0,
  boxShadow: "none",
  borderLeft: "1px solid #D8DAE5",
};

export const JD_PANEL_CARD_STYLES = {
  header: {
    flex: "0 0 auto",
    background: DETAIL_HEADER_BG,
  },
  body: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    padding: 0,
  },
};

// ─── Sticky Scale Footer ──────────────────────────────────────────────────────

export const ScaleFooterStyled = styled.div`
  flex-shrink: 0;
  padding: 10px 24px 14px;
  background: ${p => p.theme?.colors?.white ?? '#ffffff'};
  border-top: 1px solid ${p => p.theme?.colors?.border ?? '#D8DAE5'};
`;

export const ScaleHeadingStyled = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ScaleItemsRowStyled = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-top: 6px;
`;

export const ScaleItemTextStyled = styled.span`
  font-size: 13px;
`;

// ─── Pagination Footer ────────────────────────────────────────────────────────
// Sits below ScaleFooterStyled; replicates the PageTableWrapperStyled
// pagination layout so it looks identical to the Jobs Library footer.

export const PaginationFooterStyled = styled.div`
  flex-shrink: 0;
  padding: 8px 16px;
  background: ${p => p.theme?.colors?.white ?? '#ffffff'};
  border-top: 1px solid ${p => p.theme?.colors?.borderLight ?? '#E5E7EB'};

  /* Make the Ant Design Pagination fill the row with space-between */
  .ant-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 0;
  }

  /* Total text anchors left, everything else groups right */
  .ant-pagination-total-text {
    margin-right: auto;
  }

  /* Reuse the shared total styles */
  .paic-pagination-total {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

// Progress bars are now rendered via Ant Design <Progress> inside ProficiencyBar
// (see EmployeeSkillsBridgerV2.jsx). No custom bar styled-components needed.

// ─── Header description ───────────────────────────────────────────────────────

export const HeaderDescriptionStyled = styled.div`
  width: 100%;
`;

export const TourTargetStyled = styled.span`
  display: inline-flex;
`;

// ─── Column cell text ─────────────────────────────────────────────────────────

export const ContextualNotesStyled = styled(Typography.Text)`
  font-size: 14px;
`;

export const SelfReportedSubtitleStyled = styled(Typography.Text)`
  font-size: 12px;
`;

// ─── Success Alert ────────────────────────────────────────────────────────────

export const SuccessAlertStyled = styled(Alert)`
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4100;
  min-width: 360px;
`;

// ─── Shared detail-modal header style ────────────────────────────────────────
// Passed to antd's Modal `styles.header` prop, used by ScaleDetailsModal.

export const DETAIL_MODAL_HEADER_STYLE = {
  backgroundColor: DETAIL_HEADER_BG,
  padding: "16px 24px",
  margin: "-20px -24px 0",
  borderRadius: "8px 8px 0 0",
};
